import { ContentType } from "@brookbush/database";
import type { CertificationPreview, Exercise } from "@brookbush/types";

import clsx, { type ClassValue } from "clsx";
import { intervalToDuration, isPast } from "date-fns";
import { format as tzFormat, utcToZonedTime } from "date-fns-tz";
import { mutate } from "swr";
import { twMerge } from "tailwind-merge";
import { ZodOptional, type ZodType } from "zod";

/**
 * Detects the platform and returns the appropriate control key symbol
 * @returns "⌘" for macOS/iOS devices, "Ctrl" for other platforms
 */
export const getPlatformControlKey = (): string => {
	// SSR safety check
	if (typeof window === "undefined") return "Ctrl";

	const userAgent = window.navigator.userAgent;
	const platform = window.navigator.platform;

	// Check multiple indicators for macOS/iOS
	const isMac =
		/Mac|iPhone|iPad|iPod/.test(userAgent) ||
		/Mac/.test(platform) ||
		(window.navigator.maxTouchPoints > 1 && /Mac/.test(userAgent));

	return isMac ? "⌘" : "Ctrl";
};

export const createSlug = (str: string) =>
	str
		.toLowerCase()
		.trim()
		.replace(/[^\w\s-]/g, "") // Remove special characters except hyphens
		.replace(/\s+/g, "") // Replace spaces with hyphens, but keep existing hyphens intact
		.replace(/_+/g, "") // Replace spaces with hyphens, but keep existing hyphens intact
		.replace(/-{2,}/g, "-"); // Collapse multiple hyphens into a single hyphen

export const deSlugify = (str: string) =>
	str
		.toLowerCase()
		.trim()
		.replace(/-/g, ` `)
		.replace(/_/g, ` `)
		.replace(/^[a-z]/, (m) => m.toUpperCase());

export const toPascalCase = (str: string) =>
	str
		.toLowerCase()
		.trim()
		.replace(/^[a-z]/, (m) => m.toUpperCase())
		.replace(/[^a-zA-Z0-9]+(.)/g, (_m, chr) => chr.toUpperCase());

export const constructQueryString = (
	list: { key: string; value?: string | null }[],
) => {
	const queryParams = new URLSearchParams(
		list.reduce(
			(prev: { [key: string]: string }, curr) =>
				curr.value ? { ...prev, [curr.key]: curr.value } : prev,
			{},
		),
	).toString();

	return queryParams ? `?${queryParams}` : "";
};

export const clearCache = () =>
	mutate(() => true, undefined, { revalidate: false });

export const createTimerFromDate = (date: Date) => {
	const now = new Date();
	if (isPast(date)) {
		return "Event has passed";
	}

	const diff = intervalToDuration({ start: now, end: date });

	return `${diff.days} days ${diff.hours} hours ${
		diff.minutes
	} minutes ${Math.floor(diff.seconds!)} seconds`;
};

export const webinarTimeDetails = (date: Date) => {
	// For EST Time
	const estDateTime = utcToZonedTime(date, "America/New_York");
	const estWebinarTime = tzFormat(estDateTime, "hh:mm a", {
		timeZone: "America/New_York",
	});
	const estWebinarDate = tzFormat(estDateTime, "MMMM dd, yyyy", {
		timeZone: "America/New_York",
	});

	// For User's Local Time
	const userLocalTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const localDateTime = utcToZonedTime(date, userLocalTimeZone);
	const userLocalWebinarTime = tzFormat(localDateTime, "hh:mm a", {
		timeZone: userLocalTimeZone,
	});
	const userLocalWebinarDate = tzFormat(localDateTime, "MMMM dd, yyyy", {
		timeZone: userLocalTimeZone,
	});

	return {
		timer: createTimerFromDate(date),
		estWebinarTime,
		estWebinarDate,
		userLocalWebinarTime,
		userLocalWebinarDate,
	};
};

export const formatDuration = (duration?: number): string => {
	if (!duration) return "0:00";

	const hours = Math.floor(duration / 3600);
	const minutes = Math.floor((duration % 3600) / 60);
	const seconds = Math.floor(duration % 60);

	const formattedMinutes =
		hours > 0 ? String(minutes).padStart(2, "0") : String(minutes);
	const formattedSeconds = String(seconds).padStart(2, "0");

	return hours > 0
		? `${hours}:${formattedMinutes}:${formattedSeconds}`
		: `${formattedMinutes}:${formattedSeconds}`;
};

export const getAcronym = (title: string) =>
	title
		.split(" ")
		.map((i) => i[0])
		.join("")
		.toUpperCase();

export const getRangeFromOffsets = (
	container: HTMLElement,
	start: number,
	end: number,
): Range | null => {
	const range = document.createRange();
	const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);

	let offset = 0;
	let startNode: Text | null = null;
	let startOffset = 0;
	let endNode: Text | null = null;
	let endOffset = 0;

	while (walker.nextNode()) {
		const node = walker.currentNode as Text;
		const len = node.textContent?.length || 0;

		if (!startNode && offset + len >= start) {
			startNode = node;
			startOffset = start - offset;
		}

		if (!endNode && offset + len >= end) {
			endNode = node;
			endOffset = end - offset;
			break;
		}

		offset += len;
	}

	if (startNode && endNode) {
		range.setStart(startNode, startOffset);
		range.setEnd(endNode, endOffset);
		return range;
	}

	return null;
};
export function getRangeOffsetsInContainer(
	container: HTMLDivElement,
	range: Range,
): { startOffset: number; endOffset: number } | null {
	// Ensure the container is an HTMLDivElement
	if (!(container instanceof HTMLDivElement)) {
		console.error("The provided container is not an HTMLDivElement.");
		return null;
	}

	// Check if the range is within the container.
	// The range must start within or at the beginning of the container,
	// and end within or at the end of the container.
	if (
		!container.contains(range.startContainer) ||
		!container.contains(range.endContainer)
	) {
		console.warn(
			"The range is not fully contained within the specified container.",
		);
		return null;
	}

	// Create a temporary range starting at the beginning of the container.
	// This will be used as a reference point for calculating offsets.
	const tempRange = document.createRange();
	tempRange.selectNodeContents(container);
	tempRange.collapse(true); // Collapse to the start of the container

	let startOffset = 0;
	let endOffset = 0;

	try {
		// Calculate startOffset:
		// Set the end of the temporary range to the start of the actual range.
		// The length of the text content of this new temporary range will be the start offset.
		tempRange.setEnd(range.startContainer, range.startOffset);
		startOffset = tempRange.toString().length;

		// Reset temporary range to the start of the container for endOffset calculation
		tempRange.selectNodeContents(container);
		tempRange.collapse(true);

		// Calculate endOffset:
		// Set the end of the temporary range to the end of the actual range.
		// The length of the text content of this new temporary range will be the end offset.
		tempRange.setEnd(range.endContainer, range.endOffset);
		endOffset = tempRange.toString().length;
	} catch (e) {
		console.error("Error calculating range offsets:", e);
		return null;
	} finally {
		// Clean up the temporary range if necessary (though not strictly required as it's local)
		// For robust applications, especially if creating many ranges, consider detaching.
		// tempRange.detach(); // Not supported in all environments, but good practice if available
	}

	return { startOffset, endOffset };
}

export const getRandomBackgroundGradient = () => {
	const gradients = [
		"bg-gradient-to-tr from-yellow-400 via-red-500 to-pink-500",
		"bg-gradient-to-tr from-purple-400 via-pink-500 to-red-500",
		"bg-gradient-to-tr from-blue-400 via-green-500 to-yellow-500",
		"bg-gradient-to-tr from-red-400 via-yellow-500 to-green-500",
		"bg-gradient-to-tr from-pink-400 via-purple-500 to-blue-500",
		"bg-gradient-to-tr from-yellow-400 via-green-500 to-blue-500",
		"bg-gradient-to-tr from-green-400 via-blue-500 to-purple-500",
		"bg-gradient-to-tr from-blue-400 via-purple-500 to-pink-500",
	];

	return gradients[Math.floor(Math.random() * gradients.length)];
};
export const alphabets = [...Array(26)].map((_, i) =>
	String.fromCharCode(97 + i),
);

export const generateLinkedInCertUrl = (
	name: string,
	organizationId: number,
	issueYear: number,
	issueMonth: number,
	expirationYear?: number,
	expirationMonth?: number,
	certUrl?: string,
	certId?: string,
): string => {
	const baseUrl =
		"https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME";

	const params = new URLSearchParams({
		name: name,
		organizationId: organizationId.toString(),
		issueYear: issueYear.toString(),
		issueMonth: issueMonth.toString(),
	});

	if (expirationYear)
		params.append("expirationYear", expirationYear.toString());
	if (expirationMonth)
		params.append("expirationMonth", expirationMonth.toString());
	if (certUrl) params.append("certUrl", certUrl);
	if (certId) params.append("certId", certId);

	return `${baseUrl}&${params.toString()}`;
};

export const pluralize = (str: string) => {
	const lastChar = str[str.length - 1];
	return lastChar === "s" ? `${str}es` : `${str}s`;
};

export const getAcronymForCertification = (
	certification: CertificationPreview,
) => {
	/*
	 * abbreviations should be handled in the CMS using the Certification Acronym field
	 * This helper uses the old logic to match certifications to ensure acronyms are provided for
	 * older certifications that may not have them.
	 * */
	switch (true) {
		case !!certification?.acronym:
			return certification.acronym;
		case certification?.title.includes("Certified Personal Trainer"):
			return "BI-CPT";
		case certification?.title.includes("CPT Certification Study Plan"):
			return "CPT Study Plan";
		case certification?.title.includes("Integrated Manual Therapist"):
			return "IMT";
		case certification?.title.includes("Human Movement Specialist"):
			return "HMS";
		case certification?.title.includes("Strength and Performance Coach"):
			return "SPC";
		default:
			return certification?.title
				.replace("and", "")
				.split(" ")
				.map((word) => word[0])
				.join("");
	}
};

import type { StoreApi, UseBoundStore } from "zustand";

type WithSelectors<S> = S extends { getState: () => infer T }
	? S & { use: { [K in keyof T]: () => T[K] } }
	: never;

export const createSelectors = <S extends UseBoundStore<StoreApi<object>>>(
	_store: S,
) => {
	const store = _store as WithSelectors<typeof _store>;
	store.use = {};
	for (const k of Object.keys(store.getState())) {
		(store.use as any)[k] = () => store((s) => s[k as keyof typeof s]);
	}

	return store;
};

export const trialOptions: string[] = ["weekly", "monthly"];

export const scrollToElement = (elementId: string) =>
	document.getElementById(elementId)?.scrollIntoView({ behavior: "instant" });

export const capitalize = (str: string) =>
	str.charAt(0).toUpperCase() + str.slice(1);

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export const getRoutePrefix = (contentType: ContentType) =>
	contentType === ContentType.GLOSSARY
		? "glossary"
		: pluralize(contentType.toLowerCase());

export const getUserLocation = async () => {
	if (!navigator.geolocation) {
		throw new Error("Geolocation not supported by this browser.");
	}

	return new Promise((resolve, reject) => {
		navigator.geolocation.getCurrentPosition(
			({ coords }) => {
				resolve({
					lat: coords.latitude,
					lng: coords.longitude,
					accuracy: coords.accuracy,
				});
			},
			(err) => reject(new Error(err.message)),
			{
				enableHighAccuracy: true,
				timeout: 10000,
				maximumAge: 0,
			},
		);
	});
};

export const getExerciseFocus = (exercise: Exercise): string => {
	if (exercise.type === "strength_training") {
		return camelToTitle(exercise.strengthTrainingFocus);
	}
	if (exercise.type === "corrective_exercise") {
		return camelToTitle(exercise.correctiveExerciseFocus);
	}
	return camelToTitle(exercise.manualTherapyFocus);
};

export function camelToTitle(str: string): string {
	const map = {
		lphc: "LPHC",
	};

	if (map[str?.toLowerCase() as keyof typeof map]) {
		return map[str?.toLowerCase() as keyof typeof map];
	}

	return (
		str
			?.replace(/_/g, " ") // underscores → spaces
			// insert space before a capital letter only if it's preceded by a lowercase or digit
			.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
			.replace(/\(/g, " (") // add space before (
			.replace(/^./, (s) => s.toUpperCase()) // capitalize first
			// capitalize first letter inside parentheses
			.replace(/(\(\s*)([a-z])/g, (_, p, l) => p + l.toUpperCase())
	);
}

export const unwrapSchema = (
	schema: ZodType,
): {
	isOptional: boolean;
	baseSchema: ZodType;
} => {
	let baseSchema = schema as ZodType;
	let isOptional = false;

	if (baseSchema instanceof ZodOptional) {
		isOptional = true;
		baseSchema = baseSchema.unwrap() as ZodType;
	}

	return {
		baseSchema,
		isOptional,
	};
};

export const valueToString = (value: any) => {
	if (typeof value === "object" && value !== null) {
		if (value.description) return value.description;
		return JSON.stringify(value);
	}

	if (Array.isArray(value)) {
		return value.join(", ");
	}

	return value?.toString() ?? "";
};
